import { supabase } from './authService';
import { StadiumPost, AnalysisPost } from '../types';

export const communityService = {
  // 팬텀 스타디움 (SNS) - news_list 테이블의 type='stadium' 데이터 사용
  getStadiumPosts: async (limit = 20, favoriteTeamIds: string[] = []): Promise<StadiumPost[]> => {
    try {
      const { data: rawPosts, error } = await supabase
        .from('news_list')
        .select('*')
        .eq('type', 'stadium')
        .eq('is_hidden', false)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;

      // 작성자 정보 조인 (profiles 대신 user 테이블 사용)
      const userIds = Array.from(new Set((rawPosts || []).filter(n => n.user_id).map(n => n.user_id)));
      const { data: userData } = userIds.length > 0 
        ? await supabase.from('user').select('id, name, avatar_url').in('id', userIds) 
        : { data: [] };
      
      const userMap = new Map<string, any>((userData || []).map(u => [u.id, u]));

      // 좋아요 및 댓글 수 조회
      const postIds = (rawPosts || []).map(p => p.id);
      const [{ data: likesData }, { data: commentsCountData }] = await Promise.all([
        supabase.from('news_likes').select('news_id, user_id').in('news_id', postIds),
        supabase.from('news_comments')
          .select('news_id')
          .in('news_id', postIds)
          .eq('is_deleted', false)
      ]);

      const { data: { user: authUser } } = await supabase.auth.getUser();

      // news_list의 데이터를 StadiumPost 형식에 맞게 변환
      let posts = (rawPosts || []).map(item => {
        // 팀 ID 처리: teams 배열에서 첫 번째 요소를 가져오거나 team_id 사용
        let teamId = item.team_id;
        if (!teamId && item.teams && Array.isArray(item.teams) && item.teams.length > 0) {
          teamId = String(item.teams[0]);
        }

        const itemLikes = (likesData || []).filter(l => (l as any).news_id === item.id);
        const itemCommentsCount = (commentsCountData || []).filter(c => c.news_id === item.id).length;

        return {
          id: item.id,
          content: item.content_ko || item.content_en || '',
          image_url: item.image,
          created_at: item.created_at,
          user_id: item.user_id,
          team_id: teamId,
          likes_count: itemLikes.length,
          comments_count: itemCommentsCount,
          is_liked: authUser ? itemLikes.some(l => l.user_id === authUser.id) : false,
          is_hidden: item.is_hidden || false,
          user: item.user_id ? {
            name: userMap.get(item.user_id)?.name || 'Unknown',
            avatar_url: userMap.get(item.user_id)?.avatar_url || ''
          } : null
        };
      }) as StadiumPost[];

      // 관심 팀이 있는 경우 필터링 및 우선순위 조정
      if (favoriteTeamIds.length > 0) {
        // 단일 팀 선택 시 해당 팀의 포스트만 보여줌
        if (favoriteTeamIds.length === 1) {
          const selectedId = String(favoriteTeamIds[0]);
          posts = posts.filter(p => p.team_id === selectedId);
        } else {
          // 여러 팀인 경우(초기 로드 등) 우선순위 조정
          posts = [
            ...posts.filter(p => p.team_id && favoriteTeamIds.includes(p.team_id)),
            ...posts.filter(p => !p.team_id || !favoriteTeamIds.includes(p.team_id))
          ];
        }
      }

      return posts;
    } catch (error) {
      console.error('Error fetching stadium posts:', error);
      return [];
    }
  },

  deletePost: async (postId: string | number): Promise<boolean> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;

      // Ensure the user owns the post or is an admin (optional, for now just checking owner)
      const { error } = await supabase
        .from('news_list')
        .delete()
        .eq('id', postId)
        .eq('user_id', user.id);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting stadium post:', error);
      return false;
    }
  },

  reportPost: async (postId: string | number, reason: string): Promise<boolean> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;

      // Assuming a generic reports table, you might need to adjust table name if different
      const { error } = await supabase
        .from('reports')
        .insert([{
          target_id: postId,
          target_type: 'stadium_post',
          user_id: user.id,
          reason: reason
        }]);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error reporting stadium post:', error);
      throw error; // Let the UI handle already reported status if unique constraint exists
    }
  },

  createStadiumPost: async (post: Partial<StadiumPost>): Promise<boolean> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;

      const { error } = await supabase
        .from('news_list')
        .insert([{
          type: 'stadium',
          content_ko: post.content,
          image: post.image_url,
          teams: post.team_id ? [post.team_id] : [],
          user_id: user.id,
          published_at: new Date().toISOString()
        }]);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error creating stadium post:', error);
      return false;
    }
  },

  // 경기 분석 (게시판)
  getAnalysisPosts: async (limit = 20): Promise<AnalysisPost[]> => {
    try {
      const { data: rawPosts, error } = await supabase
        .from('match_analysis_posts')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;

      const userIds = Array.from(new Set((rawPosts || []).filter(n => n.user_id).map(n => n.user_id)));
      const { data: userData } = userIds.length > 0 
        ? await supabase.from('user').select('id, name, avatar_url').in('id', userIds) 
        : { data: [] };
      
      const userMap = new Map<string, any>((userData || []).map(u => [u.id, u]));

      return (rawPosts || []).map(item => ({
        ...item,
        user: item.user_id ? {
          name: userMap.get(item.user_id)?.name || 'Unknown',
          avatar_url: userMap.get(item.user_id)?.avatar_url || ''
        } : null
      })) as AnalysisPost[];
    } catch (error) {
      console.error('Error fetching analysis posts:', error);
      return [];
    }
  },

  getAnalysisPostDetail: async (id: string): Promise<AnalysisPost | null> => {
    try {
      const { data: post, error } = await supabase
        .from('match_analysis_posts')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;

      // 작성자 정보 가져오기
      let author = null;
      if (post.user_id) {
        const { data: u } = await supabase
          .from('user')
          .select('name, avatar_url')
          .eq('id', post.user_id)
          .maybeSingle();
        if (u) author = { name: u.name, avatar_url: u.avatar_url };
      }

      // 조회수 증가
      await supabase.rpc('increment_analysis_views', { post_id: id });

      return {
        ...post,
        user: author
      } as AnalysisPost;
    } catch (error) {
      console.error('Error fetching analysis post detail:', error);
      return null;
    }
  },

  createAnalysisPost: async (post: Partial<AnalysisPost>): Promise<boolean> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;

      const { error } = await supabase
        .from('match_analysis_posts')
        .insert([{
          ...post,
          user_id: user.id
        }]);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error creating analysis post:', error);
      return false;
    }
  }
};
